import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import Modal from '../common/Modal';
import './WorkReports.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WorkReports = () => {
  const { token } = useAuth();
  const socket = useSocket();
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '', 
    findings: '', 
    recommendations: '' 
  });

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setReports(data.reports || []);
      } else {
        setError(data.message || 'Failed to fetch reports');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchReports();
    socket.on('report_submitted', refresh);
    socket.on('report_status_change', refresh);
    return () => {
      socket.off('report_submitted', refresh);
      socket.off('report_status_change', refresh);
    };
  }, [socket, fetchReports]);

  const filteredReports = useMemo(() => {
    return reports.filter(r =>
      r.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r._id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, reports]);

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      alert('Title and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          contractId: selectedContract?._id,
          title: formData.title,
          description: formData.description,
          findings: formData.findings,
          recommendations: formData.recommendations
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('Report submitted successfully');
        setShowSubmitModal(false);
        setFormData({ title: '', description: '', findings: '', recommendations: '' });
        fetchReports();
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error submitting report');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending_review':
        return <span className="r-status is-pending">Pending Review</span>;
      case 'approved':
        return <span className="r-status is-approved">Approved</span>;
      case 'paid':
        return <span className="r-status is-paid">Paid & Closed</span>;
      default:
        return <span className="r-status">{status}</span>;
    }
  };

  if (loading) return <div className="r-loader-wrap"><i className="fas fa-circle-notch fa-spin"></i></div>;

  return (
    <div className="r-clean-container">
      <div className="r-clean-header">
        <div className="r-header-info">
          <h1>Work Reports</h1>
          <p>Create, submit, and audit diagnostic technical files and execution reviews</p>
        </div>
        <button className="r-action-primary-btn" onClick={() => setShowSubmitModal(true)}>
          <i className="fas fa-plus"></i> Create New Report
        </button>
      </div>

      <div className="r-toolbar-strip">
        <div className="r-search-input-wrapper">
          <i className="fas fa-search"></i>
          <input 
            type="text" 
            placeholder="Search reports by vessel name, file ID, or title..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>
        <div className="r-records-badge">
          {filteredReports.length} records
        </div>
      </div>

      {error ? (
        <div className="r-panel-state error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="r-panel-state empty">
          <i className="fas fa-file-invoice"></i>
          <h3>No technical files logged</h3>
          <p>No documentation found. Submit logs to compile service data for managers.</p>
        </div>
      ) : (
        <div className="r-table-card">
          <table className="q-table-minimal">
            <thead>
              <tr>
                <th>Report ID</th>
                <th>Vessel</th>
                <th>Report Title</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map(report => (
                <tr key={report._id}>
                  <td className="r-id-text">#{report._id?.slice(-8).toUpperCase()}</td>
                  <td className="r-vessel-name">{report.vesselName}</td>
                  <td className="r-title-text">{report.title}</td>
                  <td>{getStatusBadge(report.status)}</td>
                  <td className="text-right">
                    <div className="r-actions-row">
                      <button className="r-row-btn" title="View Document File">
                        <i className="far fa-eye"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={showSubmitModal} onClose={() => setShowSubmitModal(false)} title="Submit Service Report">
        <form onSubmit={handleSubmitReport} className="r-modal-form">
          <div className="r-form-group">
            <label>Report Title *</label>
            <input required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g., GMDSS Annual Survey Completion Log" />
          </div>
          <div className="r-form-group">
            <label>Detailed Description *</label>
            <textarea rows="4" required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Describe technical tasks performed onboard..." />
          </div>
          <div className="r-form-group">
            <label>Technical Findings</label>
            <textarea rows="3" value={formData.findings} onChange={e => setFormData({...formData, findings: e.target.value})} placeholder="Specify errors, replacements or calibrations needed..." />
          </div>
          <div className="r-form-group">
            <label>Operational Recommendations</label>
            <textarea rows="3" value={formData.recommendations} onChange={e => setFormData({...formData, recommendations: e.target.value})} placeholder="Advice for next evaluation timeline..." />
          </div>
          <div className="r-modal-actions">
            <button type="button" className="r-modal-btn secondary" onClick={() => setShowSubmitModal(false)}>Cancel</button>
            <button type="submit" className="r-modal-btn primary" disabled={submitting}>{submitting ? 'Submitting...' : 'File Report'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default WorkReports;